import React, { useState, useEffect } from 'react'
import { Search, Calendar, Package, DollarSign, Truck, CheckCircle, Home, ChevronUp, ChevronDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import CountdownTimer from '@components/ui/CountdownTimer'
import { useQuotationByToken } from '@hooks/useQuotationByToken'
import { useMetaTags } from '@hooks/useMetaTags'
import { mockData } from '@data/mockData'
import { handleCurrencyInput, currencyToStorage, storageToDisplay } from '@utils/currency'

const SupplierQuotation = () => {
  // Labels para métodos de pagamento
  const paymentMethodsLabels = {
    pix: 'PIX',
    debit: 'Débito',
    credit: 'Crédito',
    boleto: 'Boleto'
  }

  // Labels para dias da semana
  const deliveryDaysLabels = {
    monday: 'Seg',
    tuesday: 'Ter',
    wednesday: 'Qua',
    thursday: 'Qui',
    friday: 'Sex',
    saturday: 'Sáb',
    sunday: 'Dom'
  }

  const {
    quotationData,
    quotationInfo,
    searchTerm,
    filteredItems,
    errors,
    isSubmitting,
    isAlreadyResponded,
    linkToken,
    units,
    updateFormData,
    updateDeliveryDay,
    updatePaymentMethod,
    updateItem,
    setSearchTerm,
    handleSubmit,
    getUnitName,
    validateField
  } = useQuotationByToken()

  const isExpired = quotationInfo?.quotation?.expiration_date ? 
    new Date(quotationInfo.quotation.expiration_date) < new Date() : false

  const metaData = quotationInfo ? {
    title: `Cotação #${quotationInfo.quotation?.id} - ${quotationInfo.client?.name || 'Cliente'}`,
    description: `📋 Cotação solicitada por ${quotationInfo.client?.name || 'Cliente'}\n⏰ Expira em ${new Date(quotationInfo.quotation?.expiration_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}\n🚨 Urgência: ${quotationInfo.quotation?.urgency === 3 ? 'Alta' : quotationInfo.quotation?.urgency === 2 ? 'Média' : 'Baixa'}\n📦 ${quotationData.items?.length || 0} itens solicitados`,
    keywords: `cotação, ${quotationInfo.client?.name || 'cliente'}, fornecedor, pedido, ${quotationInfo.quotation?.urgency === 3 ? 'urgente' : 'normal'}`
  } : null

  useMetaTags(metaData)



  console.log(linkToken)
  if (!linkToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Link Inválido</h1>
          <p className="text-gray-600 mb-6">O link de cotação não foi encontrado ou é inválido.</p>
          <Link to="/" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <Home className="w-4 h-4 mr-2" />
            Voltar ao Início
          </Link>
        </div>
      </div>
    )
  }

  if (!quotationInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Cotação Não Encontrada</h1>
          <p className="text-gray-600 mb-6">Não foi possível encontrar os dados desta cotação.</p>
          <Link to="/" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <Home className="w-4 h-4 mr-2" />
            Voltar ao Início
          </Link>
        </div>
      </div>
    )
  }

  const QuotationRespondedBadge = () => {
    if (!isAlreadyResponded) return null
    
    return (
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
        <CheckCircle className="w-4 h-4" />
        <span className="font-semibold text-sm">Cotação Respondida</span>
      </div>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    // Criar data a partir da string ISO
    const date = new Date(dateString)
    
    // Usar métodos UTC para pegar os valores sem conversão de timezone
    const day = String(date.getUTCDate()).padStart(2, '0')
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const year = date.getUTCFullYear()
    const hours = String(date.getUTCHours()).padStart(2, '0')
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')
    
    return `${day}/${month}/${year}, ${hours}:${minutes}`
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="lg:sticky lg:top-0 lg:z-50 bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
        <div className="max-w-6xl mx-auto">
          {/* Layout Desktop */}
          <div className="hidden lg:block">
            <div className="px-4 py-6">
              {/* Linha principal com informações básicas */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-gray-800">{quotationInfo.supplier?.trade_name}</h1>
                      <p className="text-sm text-gray-600">{quotationInfo.supplier?.legal_name}</p>
                      {quotationInfo.supplier?.specialty && (
                        <p className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full inline-block mt-1">
                          {quotationInfo.supplier.specialty}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-mono text-sm font-semibold">
                      Cotação #{quotationInfo.quotation?.id}
                    </div>
                    <QuotationRespondedBadge />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {!isAlreadyResponded && <CountdownTimer targetDate={quotationInfo.quotation?.expiration_date} />}
                </div>
              </div>

              {/* Segunda linha com detalhes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm">👤</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Cliente</p>
                    <p className="text-sm font-semibold text-gray-800">{quotationInfo.client?.name}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Data Solicitação</p>
                    <p className="text-sm font-semibold text-gray-800">{formatDate(quotationInfo.quotation?.created_at)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 text-sm">⏱️</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Urgência</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {quotationInfo.quotation?.urgency === 1 && 'Baixa'}
                      {quotationInfo.quotation?.urgency === 2 && 'Média'}
                      {quotationInfo.quotation?.urgency === 3 && 'Alta'}
                      {quotationInfo.quotation?.urgency === 4 && 'Crítica'}
                      {quotationInfo.quotation?.urgency === 5 && 'Emergencial'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Layout Mobile Simplificado */}
          <div className="lg:hidden">
            <div className="px-4 py-4">
              {/* Informações principais */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg font-bold text-gray-800 truncate">{quotationInfo.supplier?.trade_name}</h1>
                  <p className="text-sm text-gray-600 truncate">{quotationInfo.supplier?.legal_name}</p>
                  {quotationInfo.supplier?.specialty && (
                    <p className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full inline-block mt-1">
                      {quotationInfo.supplier.specialty}
                    </p>
                  )}
                </div>
              </div>

              {/* Badges e Timer */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg font-mono text-sm font-semibold">
                  Cotação #{quotationInfo.quotation?.id}
                </div>
                <QuotationRespondedBadge />
                {!isAlreadyResponded && (
                  <div className="ml-auto">
                    <CountdownTimer targetDate={quotationInfo.quotation?.expiration_date} />
                  </div>
                )}
              </div>

              {/* Informações adicionais em layout compacto */}
              <div className="space-y-3 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-green-600 text-sm">👤</span>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Cliente:</span>
                  <span className="text-sm font-semibold text-gray-800">{quotationInfo.client?.name}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Solicitação:</span>
                  <span className="text-sm font-semibold text-gray-800">{formatDate(quotationInfo.quotation?.created_at)}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-purple-600 text-sm">⏱️</span>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Urgência:</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {quotationInfo.quotation?.urgency === 1 && 'Baixa'}
                    {quotationInfo.quotation?.urgency === 2 && 'Média'}
                    {quotationInfo.quotation?.urgency === 3 && 'Alta'}
                    {quotationInfo.quotation?.urgency === 4 && 'Crítica'}
                    {quotationInfo.quotation?.urgency === 5 && 'Emergencial'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Card de Cotação Expirada */}
        {isExpired && (
          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl shadow-lg p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-3xl">⏰</span>
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-2">Cotação Expirada</h2>
            <p className="text-red-100 text-lg">
              Esta cotação expirou em {formatDate(quotationInfo.quotation?.expiration_date)}
            </p>
            <p className="text-red-100 mt-2">
              Não é mais possível responder a esta cotação.
            </p>
          </div>
        )}

        {/* Mensagem para cotação já respondida - Movida para o topo */}
        {isAlreadyResponded && (
          <div className="flex justify-center mb-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center max-w-md">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-800 mb-2">Cotação Já Respondida</h3>
              <p className="text-green-700 text-sm">
                Esta cotação foi respondida anteriormente. Os dados estão sendo exibidos apenas para visualização.
              </p>
            </div>
          </div>
        )}

        {/* Formulário de Cotação - Oculto se expirado */}
        {!isExpired && (
          <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Valor Mínimo do Pedido */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold text-gray-800 text-sm">Valor Mínimo</h3>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R$</span>
              <input
                type="text"
                placeholder="0,00"
                readOnly={isAlreadyResponded}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-gray-900 ${
                  isAlreadyResponded 
                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed' 
                    : errors.minimumOrderValue 
                      ? 'border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-green-500'
                }`}
                value={storageToDisplay(quotationData.minimumOrderValue)}
                onChange={(e) => {
                  if (!isAlreadyResponded) {
                    const formatted = handleCurrencyInput(e.target.value)
                    const storage = currencyToStorage(formatted)
                    updateFormData('minimumOrderValue', storage)
                  }
                }}
                onBlur={(e) => {
                  if (!isAlreadyResponded) {
                    validateField('minimumOrderValue', e.target.value)
                  }
                }}
              />
            </div>
            {errors.minimumOrderValue && (
              <p className="text-red-500 text-sm mt-2">{errors.minimumOrderValue}</p>
            )}
          </div>

          {/* Métodos de Pagamento */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-800 text-sm">Métodos de Pagamento</h3>
            </div>
            <div className="flex flex-col gap-2">
              {Object.entries(paymentMethodsLabels).map(([key, label]) => (
                <label key={key} className={`flex items-center gap-2 ${isAlreadyResponded ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={quotationData.payment.includes(key)}
                    disabled={isAlreadyResponded}
                    onChange={() => !isAlreadyResponded && updatePaymentMethod(key)}
                  />
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                    isAlreadyResponded
                      ? quotationData.payment.includes(key)
                        ? 'bg-gray-400 border-gray-400 text-white'
                        : 'bg-gray-100 border-gray-300 text-gray-400'
                      : quotationData.payment.includes(key)
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : errors.payment
                          ? 'border-red-300 text-gray-600 hover:border-red-400'
                          : 'border-gray-300 text-gray-600 hover:border-orange-300'
                  }`}>
                    <CheckCircle className={`w-2.5 h-2.5 ${quotationData.payment.includes(key) ? 'block' : 'hidden'}`} />
                  </div>
                  <span className={`text-xs font-medium ${
                    isAlreadyResponded ? 'text-gray-500' : 'text-gray-700'
                  }`}>{label}</span>
                </label>
              ))}
            </div>
            {errors.payment && (
              <p className="text-red-500 text-sm mt-3">{errors.payment}</p>
            )}
          </div>

          {/* Prazo de Entrega */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500 min-w-[280px]">
            <div className="flex items-center gap-3 mb-4">
              <Truck className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-gray-800 text-sm">Prazo de Entrega</h3>
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="24"
                readOnly={isAlreadyResponded}
                className={`w-20 py-3 px-4 border rounded-lg focus:ring-2 focus:border-transparent text-gray-900 ${
                  isAlreadyResponded 
                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed' 
                    : errors.deliveryTerm 
                      ? 'border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-purple-500'
                }`}
                value={quotationData.deliveryTerm}
                onChange={(e) => !isAlreadyResponded && updateFormData('deliveryTerm', e.target.value)}
                onBlur={(e) => {
                  if (!isAlreadyResponded) {
                    validateField('deliveryTerm', e.target.value)
                  }
                }}
              />
              <select
                disabled={isAlreadyResponded}
                className={`flex-1 py-3 px-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-gray-900 ${
                  isAlreadyResponded 
                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300' 
                    : 'border-gray-300 focus:ring-purple-500'
                }`}
                value={quotationData.deliveryTimeUnit}
                onChange={(e) => !isAlreadyResponded && updateFormData('deliveryTimeUnit', e.target.value)}
              >
                <option value="hours">horas</option>
                <option value="days">dias</option>
              </select>
            </div>
            {errors.deliveryTerm && (
              <p className="text-red-500 text-sm mt-1">{errors.deliveryTerm}</p>
            )}
          </div>

            {/* Prazo de Pagamento - Condicional (apenas se boleto for selecionado) */}
            {quotationData.payment.includes('boleto') && (
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-gray-800 text-sm">Prazo de Pagamento</h3>
                </div>
                <div className="relative">
                   <input
                     type="number"
                     placeholder="30"
                     readOnly={isAlreadyResponded}
                     className={`w-full pr-12 py-3 px-4 border rounded-lg focus:ring-2 focus:border-transparent text-gray-900 ${
                       isAlreadyResponded 
                         ? 'bg-gray-100 text-gray-600 cursor-not-allowed' 
                         : errors.paymentTerm 
                           ? 'border-red-500 focus:ring-red-500' 
                           : 'border-gray-300 focus:ring-blue-500'
                     }`}
                     value={quotationData.paymentTerm}
                     onChange={(e) => !isAlreadyResponded && updateFormData('paymentTerm', e.target.value)}
                     onBlur={(e) => {
                       if (!isAlreadyResponded) {
                         validateField('paymentTerm', e.target.value)
                       }
                     }}
                   />
                   <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">dias</span>
                 </div>
                 {errors.paymentTerm && (
                   <p className="text-red-500 text-sm mt-1">{errors.paymentTerm}</p>
                 )}
              </div>
            )}

            {/* Dias de Entrega */}
            <div className="bg-white rounded-xl shadow-md p-6 col-span-1 md:col-span-2 lg:col-span-4">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-5 h-5 text-indigo-500" />
                <h3 className="font-semibold text-gray-800">Dias de Entrega</h3>
              </div>
              <div className="grid grid-cols-7 gap-3 sm:gap-4">
                {Object.entries(deliveryDaysLabels).map(([key, label]) => (
                  <label key={key} className={`flex flex-col items-center ${isAlreadyResponded ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={quotationData.deliveryDays[key]}
                      disabled={isAlreadyResponded}
                      onChange={() => !isAlreadyResponded && updateDeliveryDay(key)}
                    />
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 flex items-center justify-center transition-all ${
                      isAlreadyResponded
                        ? quotationData.deliveryDays[key]
                          ? 'bg-gray-400 border-gray-400 text-white'
                          : 'bg-gray-100 border-gray-300 text-gray-400'
                        : quotationData.deliveryDays[key]
                          ? 'bg-indigo-500 border-indigo-500 text-white'
                          : errors.deliveryDays
                            ? 'border-red-300 text-gray-600 hover:border-red-400'
                            : 'border-gray-300 text-gray-600 hover:border-indigo-300'
                    }`}>
                      <CheckCircle className={`w-3 h-3 sm:w-4 sm:h-4 ${quotationData.deliveryDays[key] ? 'block' : 'hidden'}`} />
                    </div>
                    <span className={`text-xs mt-1 font-medium ${
                      isAlreadyResponded ? 'text-gray-500' : 'text-gray-800'
                    }`}>{label}</span>
                  </label>
                ))}
              </div>
              {errors.deliveryDays && (
                <p className="text-red-500 text-sm mt-3">{errors.deliveryDays}</p>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Lista de Itens - Oculta se expirado */}
        {!isExpired && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Itens para Cotação</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar item..."
                readOnly={isAlreadyResponded}
                className={`pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-gray-900 ${
                  isAlreadyResponded 
                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300' 
                    : 'border-gray-300 focus:ring-indigo-500'
                }`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4">
            {filteredItems.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Info do Item */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-medium text-gray-800">{item.name.charAt(0).toUpperCase() + item.name.slice(1)}</h4>
                      <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs font-medium">
                        Item #{item.id}
                      </span>
                      {item.quantity && (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                          Qtd. solicitada: {item.quantity} {getUnitName(item.unitOfMeasureId)}
                        </span>
                      )}
                      {item.notes && (
                        <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-medium">
                          Marca: {item.notes}
                        </span>
                      )}
                    </div>
                    
                    {/* Toggle de Disponibilidade */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-sm font-medium ${!item.available ? 'text-red-600' : 'text-gray-500'}`}>
                        Não disponível
                      </span>
                      <button
                        type="button"
                        disabled={isAlreadyResponded}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                          isAlreadyResponded
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                        } ${
                          item.available 
                            ? 'bg-green-500' 
                            : 'bg-gray-300'
                        }`}
                        onClick={() => !isAlreadyResponded && updateItem(item.id, 'available', !item.available)}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            item.available ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className={`text-sm font-medium ${item.available ? 'text-green-600' : 'text-gray-500'}`}>
                        Disponível
                      </span>
                    </div>
                  </div>

                  {/* Inputs */}
                   {item.available && (
                     <div className="flex-1 lg:flex-none lg:w-full lg:max-w-2xl">
                       <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                         {/* Unidade */}
                         <div>
                           <label className="block text-xs font-medium text-gray-700 mb-1">Unidade</label>
                           <select
                             disabled={isAlreadyResponded}
                             className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-gray-900 ${
                               isAlreadyResponded 
                                 ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300' 
                                 : errors[`item_${item.id}`]?.unitOfMeasureId
                                   ? 'border-red-500 focus:ring-red-500'
                                   : 'border-gray-300 focus:ring-indigo-500'
                             }`}
                             value={item.unitOfMeasureId}
                             onChange={(e) => !isAlreadyResponded && updateItem(item.id, 'unitOfMeasureId', e.target.value)}
                           >
                             <option value="">Selecione...</option>
                             {(units.length > 0 ? units : mockData.unitsOfMeasure).map(unit => (
                               <option key={unit.id} value={unit.id}>
                                 {unit.name} ({unit.abbreviation})
                               </option>
                             ))}
                           </select>
                           {errors[`item_${item.id}`]?.unitOfMeasureId && (
                             <p className="text-red-500 text-xs mt-1">{errors[`item_${item.id}`].unitOfMeasureId}</p>
                           )}
                         </div>
                         
                         {/* Marca */}
                         <div>
                           <label className="block text-xs font-medium text-gray-700 mb-1">Marca</label>
                           <input
                             type="text"
                             placeholder="Digite a marca..."
                             readOnly={isAlreadyResponded}
                             className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-gray-900 ${
                               isAlreadyResponded 
                                 ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300' 
                                 : errors[`item_${item.id}`]?.brand
                                   ? 'border-red-500 focus:ring-red-500'
                                   : 'border-gray-300 focus:ring-indigo-500'
                             }`}
                             value={item.brand}
                             onChange={(e) => !isAlreadyResponded && updateItem(item.id, 'brand', e.target.value)}
                           />
                           {errors[`item_${item.id}`]?.brand && (
                             <p className="text-red-500 text-xs mt-1">{errors[`item_${item.id}`].brand}</p>
                           )}
                         </div>
                         
                         {/* Valor Unitário */}
                         <div>
                           <label className="block text-xs font-medium text-gray-700 mb-1">Valor Unitário</label>
                           <div className="relative">
                             <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">R$</span>
                             <input
                               type="text"
                               placeholder="0,00"
                               readOnly={isAlreadyResponded}
                               className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-gray-900 ${
                                 isAlreadyResponded 
                                   ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300' 
                                   : errors[`item_${item.id}`]?.unitPrice
                                     ? 'border-red-500 focus:ring-red-500'
                                     : 'border-gray-300 focus:ring-indigo-500'
                               }`}
                               value={storageToDisplay(item.unitPrice)}
                               onChange={(e) => {
                                 if (!isAlreadyResponded) {
                                   const formatted = handleCurrencyInput(e.target.value)
                                   const storageValue = currencyToStorage(formatted)
                                   updateItem(item.id, 'unitPrice', storageValue)
                                 }
                               }}
                             />
                           </div>
                           {errors[`item_${item.id}`]?.unitPrice && (
                             <p className="text-red-500 text-xs mt-1">{errors[`item_${item.id}`].unitPrice}</p>
                           )}
                         </div>
                       </div>
                       
                       {/* Observações */}
                       <div className="mt-3">
                         <label className="block text-xs font-medium text-gray-700 mb-1">Observações</label>
                         <textarea
                           placeholder="Informações adicionais..."
                           rows={2}
                           readOnly={isAlreadyResponded}
                             className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-gray-900 resize-none ${
                               isAlreadyResponded 
                                 ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300' 
                                 : 'border-gray-300 focus:ring-indigo-500'
                             }`}
                           value={item.observations}
                           onChange={(e) => !isAlreadyResponded && updateItem(item.id, 'observations', e.target.value)}
                         />
                       </div>
                     </div>
                   )}
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum item encontrado com esse nome.</p>
            </div>
          )}
        </div>
        )}

        {/* Mensagem de erro geral */}
        {errors.items && !isExpired && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm font-medium">{errors.items}</p>
          </div>
        )}

        {/* Botão de Envio - Oculto se já foi respondida ou expirada */}
        {!isAlreadyResponded && !isExpired && (
          <div className="flex justify-center">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`font-semibold py-4 px-8 rounded-xl shadow-lg transition-all flex items-center gap-3 ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white hover:shadow-xl transform hover:scale-105'
              }`}
            >
              <CheckCircle className="w-5 h-5" />
              {isSubmitting ? 'Enviando...' : 'Enviar Cotação'}
            </button>
          </div>
        )}
      </main>
    </div>
  )
}

export default SupplierQuotation